import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';

import cloneDeep from 'lodash.clonedeep';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { PaginationData, PaginationRange } from 'tiime-material';

import { BusinessUserApiService } from '@api-services/business-user-api.service';
import { Role } from '@enums/role.enum';
import { ProspectApiService } from '@gi/api/services/prospect-api.service';
import { ProspectsFilters } from '@gi/interfaces/prospects-filters';
import { Prospect } from '@gi/models/prospect';
import { ProspectTotal } from '@gi/models/prospect-total';
import { DateHelper } from '@helpers/date.helper';
import { BusinessUnit } from '@models/business-unit';
import { BusinessUser } from '@models/business-user';
import { Company } from '@models/company';
import { NavigationService } from '@modules/core/navigation/navigation.service';
import { ProspectsHelper } from '@modules/opportunities/prospects/prospects.helper';
import {
  ExternalUrlService,
  GOOGLE_CALENDAR_DATE_FORMAT,
  GoogleCalendarParams,
  GoogleContactsParams
} from '@services/external-url.service';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { CreateCompanyDialogData } from '@shared-dialogs/create-company-dialog/create-company-dialog-data';
import { CreateCompanyDialogService } from '@shared-dialogs/create-company-dialog/create-company-dialog.service';
import { CreateOrUpdateProspectDialogService } from '@shared-dialogs/create-or-update-prospect-dialog/create-or-update-prospect-dialog.service';

import { FacadeBase } from './facade.base';

@Injectable({
  providedIn: 'root'
})
export class ProspectFacade extends FacadeBase {
  constructor(
    private readonly prospectApiService: ProspectApiService,
    private readonly createOrUpdateProspectDialogService: CreateOrUpdateProspectDialogService,
    private readonly createCompanyDialogService: CreateCompanyDialogService,
    private readonly businessUserApiService: BusinessUserApiService,
    private readonly externalUrlService: ExternalUrlService,
    private readonly navigationService: NavigationService
  ) {
    super();
  }

  getProspect(prospectId: number): Observable<Prospect> {
    return this.prospectApiService.getProspect(prospectId);
  }

  createProspect(prospect: Prospect): Observable<Prospect> {
    return this.prospectApiService.createProspect(prospect);
  }

  updateProspect(prospect: Prospect): Observable<Prospect> {
    return this.prospectApiService.updateProspect(prospect);
  }

  deleteProspect(prospectId: number): Observable<unknown> {
    return this.prospectApiService.deleteProspect(prospectId);
  }

  getProspectsTotals(): Observable<ProspectTotal> {
    return this.prospectApiService.getProspectsTotals();
  }

  updateProspectInternalComment(prospectId: number, comment: string): Observable<Prospect> {
    return this.prospectApiService.updateProspectInternalComment(prospectId, comment);
  }

  getProspects(
    pagination: PaginationRange,
    search?: string,
    sort?: Sort,
    filters?: ProspectsFilters
  ): Observable<PaginationData<Prospect>> {
    return this.prospectApiService.getProspects(pagination, search, sort, ProspectsHelper.toParams(filters));
  }

  getCompanyProspects(pagination: PaginationRange, search: string, sort: Sort): Observable<PaginationData<Prospect>> {
    return this.prospectApiService.getCompanyProspects(pagination, search, sort);
  }

  searchProspectsByEmailOrPhone(email: string, phone: string): Observable<Prospect[]> {
    return this.prospectApiService.searchProspectsByEmailOrPhone({ email, phone });
  }

  duplicateProspect(prospect: Prospect): Observable<Prospect> {
    const duplicatedProspect: Prospect = new Prospect();
    duplicatedProspect.firstName = prospect.firstName;
    duplicatedProspect.lastName = prospect.lastName;
    duplicatedProspect.email = prospect.email;
    duplicatedProspect.phone = prospect.phone;
    duplicatedProspect.civility = prospect.civility;
    duplicatedProspect.mailingStreet = prospect.mailingStreet;
    duplicatedProspect.mailingCity = prospect.mailingCity;
    duplicatedProspect.mailingPostalCode = prospect.mailingPostalCode;
    duplicatedProspect.companyLegalForm = prospect.companyLegalForm;
    duplicatedProspect.companyName = prospect.companyName;
    duplicatedProspect.internalComment = prospect.internalComment;

    return this.openCreateProspectDialog(duplicatedProspect);
  }

  createCompany(prospect: Prospect): Observable<Company> {
    return this.getBusinessUserBusinessUnits().pipe(
      switchMap((businessUnits: BusinessUnit[]) => {
        if (businessUnits?.length === 0) {
          this.snackbar.openError(
            'La création de dossier depuis l’interface Tiime n’est pas autorisée pour vos groupes'
          );
          return of(null);
        }
        return this.openCreateCompanyDialog(businessUnits, prospect);
      })
    );
  }

  openCreateProspectDialog(prospect: Prospect): Observable<Prospect> {
    return this.openCreateOrUpdateProspectDialog(prospect);
  }

  openUpdateProspectDialog(prospectId: number, displayNavigateToCompany?: boolean): Observable<Prospect> {
    return this.getProspect(prospectId).pipe(
      switchMap((prospect: Prospect) => this.openCreateOrUpdateProspectDialog(prospect, displayNavigateToCompany))
    );
  }

  addToGoogleCalendar(prospect: Prospect): void {
    if (!prospect.appointmentDate) {
      return;
    }

    const startDate = prospect.appointmentDate;
    const endDate = DateHelper.add(startDate, 45, 'minute');

    const params: GoogleCalendarParams = {
      text: `Rendez-vous ${prospect.fullName}`,
      add: prospect.email,
      dates: `${DateHelper.format(startDate, GOOGLE_CALENDAR_DATE_FORMAT)}/${DateHelper.format(
        endDate,
        GOOGLE_CALENDAR_DATE_FORMAT
      )}`
    };

    this.externalUrlService.openGoogleCalendar(params);
  }

  addToGoogleContacts(prospect: Prospect): void {
    const params: GoogleContactsParams = {
      email: prospect.email ?? '',
      familyname: prospect.lastName ?? '',
      givenname: prospect.firstName ?? '',
      phone: prospect.phone ?? ''
    };

    this.externalUrlService.openGoogleContacts(params);
  }

  navigateToCompanyContractsInNewTab(prospect: Prospect): void {
    this.navigationService.navigateToCompanyContractsInNewTab(prospect.company.id);
  }

  openConfirmDeleteProspectDialog(prospect: Prospect): Observable<Prospect> {
    const data: ConfirmDialogData = new ConfirmDialogData();
    data.confirmMessage = `Êtes-vous sûr de vouloir supprimer le prospect ${prospect.fullName} ?`;

    return this.confirmDialogService
      .open({ data })
      .afterClosed()
      .pipe(
        filter((confirm: boolean) => confirm),
        switchMap(() => this.deleteProspect(prospect.id)),
        map(() => prospect),
        tap(() => this.snackbar.openSuccess('Le prospect a bien été supprimé'))
      );
  }

  private openCreateOrUpdateProspectDialog(
    prospect: Prospect,
    displayNavigateToCompany?: boolean
  ): Observable<Prospect> {
    return this.createOrUpdateProspectDialogService
      .open({
        data: {
          prospect,
          displayNavigateToCompany
        }
      })
      .afterClosed()
      .pipe(filter((createdOrUpdatedProspect: Prospect) => !!createdOrUpdatedProspect));
  }

  private openCreateCompanyDialog(businessUnits: BusinessUnit[], selectedProspect: Prospect): Observable<Company> {
    const prospect: Prospect = cloneDeep(selectedProspect);
    if (prospect.businessUnit.archivedAt) {
      prospect.businessUnit = null;
    }
    const data: CreateCompanyDialogData = {
      mode: 'Expert',
      businessUnits,
      prospect
    };

    return this.createCompanyDialogService
      .open({
        data
      })
      .afterClosed();
  }

  private getBusinessUserBusinessUnits(): Observable<BusinessUnit[]> {
    return this.getBusinessUser().pipe(
      switchMap((businessUser: BusinessUser) =>
        businessUser.roles.includes(Role.PARTNER_SALES)
          ? this.businessUserApiService.getBusinessUserPartnerBusinessUnits()
          : this.businessUserApiService.getBusinessUserBusinessUnits(true)
      )
    );
  }
}
